import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Fingerprint, ShieldCheck, X, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useCustomerAuth, API } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/use-toast';
import {
  passkeySupported,
  isPlatformAuthenticatorAvailable,
  registerPasskey,
} from '../lib/passkey';

const DISMISS_KEY = 'mr_passkey_prompt_dismissed';

const PasskeyEnrollPrompt = () => {
  const { token, email, authHeaders } = useCustomerAuth();
  const { toast } = useToast();
  const [eligible, setEligible] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return parsed?.email === email;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token || !email) return;
      if (!passkeySupported()) return;
      const platform = await isPlatformAuthenticatorAvailable();
      if (cancelled || !platform) return;
      // Skip if user already has passkeys on this account
      try {
        const { data } = await axios.get(`${API}/customer/passkeys`, { headers: authHeaders });
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setEligible(false);
          return;
        }
      } catch {
        // best-effort
      }
      if (!cancelled) setEligible(true);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token, email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnroll = async () => {
    setLoading(true);
    try {
      await registerPasskey(authHeaders);
      setEnrolled(true);
      setEligible(false);
      toast({
        title: 'Passkey saved',
        description: 'Next time, sign in instantly with Face ID / Touch ID.',
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not register passkey';
      // Swallow user-cancel quietly
      if (!/cancel|abort|NotAllowed/i.test(msg)) {
        toast({ title: 'Passkey setup failed', description: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify({ email, at: Date.now() }));
    } catch {
      // ignore
    }
  };

  if (!eligible || dismissed) return null;

  return (
    <div
      className="rounded-2xl border border-[#0078D4]/20 bg-gradient-to-br from-[#DEECF9] to-white p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-4 shadow-sm"
      data-testid="passkey-enroll-prompt"
    >
      <div className="w-12 h-12 rounded-xl bg-[#0078D4] text-white flex items-center justify-center flex-shrink-0">
        {enrolled ? <Check className="w-6 h-6" /> : <Fingerprint className="w-6 h-6" />}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-[#0F172A] text-base">
          Sign in faster with Face ID / Touch ID
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Skip the 4-digit code next time. Use your device's built-in biometrics — your face,
          fingerprint, or device PIN — to sign in instantly. Nothing leaves this device.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            onClick={handleEnroll}
            disabled={loading}
            className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white"
            data-testid="passkey-enroll-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Fingerprint className="w-4 h-4 mr-2" />
            )}
            Set up passkey
          </Button>
          <button
            onClick={handleDismiss}
            disabled={loading}
            className="text-sm text-slate-500 hover:text-slate-700 font-medium"
            data-testid="passkey-dismiss-btn"
          >
            Maybe later
          </button>
          <span className="flex items-center gap-1 text-xs text-slate-500 ml-auto">
            <ShieldCheck className="w-3.5 h-3.5" />
            Phishing-resistant
          </span>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="p-1 text-slate-400 hover:text-slate-700 absolute-tl sm:relative"
        aria-label="Dismiss"
        data-testid="passkey-close-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PasskeyEnrollPrompt;
